import { useCallback, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import type { RootState } from '../../../state/store';
import { useSub } from '../../../helpers/hooks/pubSub';
import { getTokenUsage, type TokenUsageStats } from '../services/api';

export interface TokenUsageState {
  tokenUsage: TokenUsageStats | null;
  loading: boolean;
  isDemo: boolean;
  /** True when the server reports the company is over its total budget. */
  isExceeded: boolean;
  /** True when warning threshold is reached (≥90% daily free used) and no purchased pool in use. */
  isWarning: boolean;
  refresh: () => Promise<void>;
  /**
   * Optimistically patch the total balance from an SSE :done event (O8).
   * Only updates `total_available_tokens` — the subsequent `refresh()` call
   * fills in the full breakdown (daily_used, percent, etc).
   */
  applyBalancePatch: (balanceRemaining: number | null | undefined) => void;
}

/**
 * Owns the AskVantage V2 token-usage state.
 *
 * - Reuses the V1 endpoint `GET /api/v1/ask-vantage/token-usage` (unified backend)
 * - Subscribes to the existing `ask_vantage_token_balance_updated` pubsub event
 *   forwarded by `CompanyEvents` from the company-level Pusher channel
 * - Callers can trigger `refresh()` after a stream completes so the UI reflects
 *   the tokens consumed by the last assistant turn without waiting for Pusher.
 *
 * Ready-flag (`ready`) gates the initial fetch so we don't pre-load before the
 * rest of the AskVantage UI knows the company has access.
 */
export const useTokenUsage = (ready: boolean): TokenUsageState => {
  const [tokenUsage, setTokenUsage] = useState<TokenUsageStats | null>(null);
  const [loading, setLoading] = useState(true);

  const isDemo = useSelector(
    (state: RootState) => Boolean((state.company?.company_details as any)?.demo_account)
  );

  const refresh = useCallback(async () => {
    try {
      const res = await getTokenUsage();
      setTokenUsage(res.current_usage);
    } catch {
      // Non-critical: silently ignore. UI falls back to "—".
    } finally {
      setLoading(false);
    }
  }, []);

  // Optimistically update the displayed balance from SSE. The HTTP refresh
  // that fires on streamStatus === 'done' will still reconcile everything,
  // but this trims ~1 RTT of perceived lag between the LLM finishing and
  // the meter moving.
  const applyBalancePatch = useCallback(
    (balanceRemaining: number | null | undefined) => {
      if (balanceRemaining == null) return;
      setTokenUsage((prev) => {
        if (!prev) return prev;
        return { ...prev, total_available_tokens: balanceRemaining };
      });
    },
    []
  );

  // Initial fetch. Runs once the container signals it's safe to load.
  useEffect(() => {
    if (!ready) return;
    refresh();
  }, [ready, refresh]);

  // `useSub` only binds once on mount (its deps are []), so wrap refresh in a
  // ref to always call the latest function when the event fires.
  const refreshRef = useRef(refresh);
  useEffect(() => {
    refreshRef.current = refresh;
  }, [refresh]);

  useSub('ask_vantage_token_balance_updated', () => {
    refreshRef.current();
  });

  // O8: Optimistic pre-refresh patch fired from useConversation when the
  // orchestrator's SSE :done carries balance_remaining. The full refresh
  // still runs from AskV2Container; this just removes the visual lag.
  const patchRef = useRef(applyBalancePatch);
  useEffect(() => {
    patchRef.current = applyBalancePatch;
  }, [applyBalancePatch]);

  useSub('ask_vantage_v2_balance_patch', (balance: number) => {
    patchRef.current(balance);
  });

  const isExceeded = Boolean(tokenUsage?.is_quota_exceeded);
  const isWarning =
    !isExceeded &&
    Boolean(tokenUsage?.show_warning) &&
    (tokenUsage?.remaining_purchased_tokens ?? 0) <= 0;

  return {
    tokenUsage,
    loading,
    isDemo,
    isExceeded,
    isWarning,
    refresh,
    applyBalancePatch,
  };
};
